import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Hidden,
  IconButton,
  InputLabel,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Box,
  Card,
  CardContent,
  CardActions,
  Divider,
  Tabs,
  Tab,
  ButtonBase,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import EditIcon from '@material-ui/icons/Edit';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import DescriptionIcon from '@material-ui/icons/Description';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import StorageIcon from '@material-ui/icons/Storage';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import useApi from '../../../utils/useApi';
import validationSchema from './validationSchema';
import UIButtonSearch from '../../UI/Button/Search/Search';
import UIModalFilter from '../../UI/ModalFilter/UIModalFilter';
import UIContainer from '../../UI/Container/UIContainer';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import {
  emptyObject,
  capitalize,
  clearObject,
  formattedCurrency,
  isEqualObject,
  calculateMarkupByValue,
  calculateMarginByValue,
  calculateProfitByValue,
  calculateValueByMargin,
  parseImages,
  formatStockAmount,
} from '../../../utils/utils';
import UIFilter from '../../UI/Button/Filter/UIFilter';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import UITablePagination from '../../UI/Table/Pagination/UITablePagination';
import SupplierCreateModal from '../../Supplier/SupplierCreateModal';
import AdministrativeAdjustmentModal from '../../UI/Modal/AdministrativeAdjustment/AdministrativeAdjustmentModal';
import ProductImportModal from './ProductImportModal';
import UIImageUpload from '../../UI/ImageUpload/UIImageUpload';
import UIDuxiaDescriptionButton from '../../UI/Duxia/UIDuxiaDescriptionButton';
import UIBarcodeRegenerateButton from '../../UI/Barcode/UIBarcodeRegenerateButton';
import PrintIcon from '@material-ui/icons/Print';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import { ProductSinglePrintModal, CategoryBulkPrintModal } from '../LabelPrint';
import { ScaleExportModal } from '../ScaleExport';
import UIBatchActionBar from '../../UI/BatchActionBar/UIBatchActionBar';
import ProductSaleFormatCard from '../Common/ProductSaleFormatCard';
import ProductSaleFormatModal from '../Common/ProductSaleFormatModal';
import ProductUnitSelect from '../Common/ProductUnitSelect';
import { PRODUCT_UNITS, normalizeUnitCode, isFractionalUnit, getUnitInputStep } from '../../../constants/productUnits';

const supplierFilter = createFilterOptions();

const useStyles = makeStyles((theme) => ({
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
  tableHead: {
    backgroundColor: theme.palette.grey[50],
  },
  tableHeadCell: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  rowLacking: {
    backgroundColor: '#fff0f1',
  },
  rowNormal: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  marginHigh: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  marginLow: {
    color: theme.palette.warning.main,
    fontWeight: 'bold',
  },
  actionBtn: {
    padding: 8,
  },
  dialogPaper: {
    borderRadius: 12,
    minHeight: 620,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      height: '100%',
      minHeight: '100%',
      maxHeight: 'none',
      borderRadius: 0,
    },
  },
  dialogContentStable: {
    flex: 1,
    minHeight: 460,
    padding: theme.spacing(2.5, 3),
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 1.5),
      minHeight: 'auto',
    },
  },
  tabRoot: {
    minHeight: 52,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
      padding: '6px 4px',
      minWidth: 'auto',
    },
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: theme.spacing(2),
  },
  profitBadgePositive: {
    backgroundColor: '#e6f4ea',
    color: '#137333',
    fontWeight: 'bold',
    padding: '4px 10px',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  profitBadgeNegative: {
    backgroundColor: '#fce8e6',
    color: '#c5221f',
    fontWeight: 'bold',
    padding: '4px 10px',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  subSectionCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
    marginTop: theme.spacing(1),
  },
  segmentedContainer: {
    display: 'flex',
    backgroundColor: '#f1f3f6',
    padding: 4,
    borderRadius: 8,
    border: '1px solid #e2e5e9',
    marginBottom: theme.spacing(1.5),
  },
  segmentedButton: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      color: '#334155',
      backgroundColor: 'rgba(255,255,255,0.4)',
    },
  },
  segmentedButtonActive: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    color: '#17497F',
    fontWeight: 700,
    fontSize: '0.85rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: '100%',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  headerActionBtn: {
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      flex: '1 1 auto',
      minWidth: 0,
      padding: '5px 8px',
      fontSize: '0.75rem',
      '& .MuiButton-startIcon': {
        marginRight: 4,
        marginLeft: -2,
      },
    },
  },
}));

const initialState = {
  id: '',
  name: '',
  description: '',
  category: '',
  particulars: '',
  cost: '',
  value: '',
  amount: '',
  barCode: '',
  supplier: '',
  unit: '',
  lacking: 0,
  showInCatalog: false,
  margin: 0,
  markup: 0,
  images: [],
  childFormats: [],
  parentProductId: null,
  fractionFactor: null,
  parentName: '',
};

const addAmount = {
  id: null,
  amount: 1,
  cost: 0,
  costTotal: 0,
};

const initialClick = {
  mouseX: null,
  mouseY: null,
};

const ProductSearch = (children) => {
  const { referenceId } = children;
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const [alert, setAlert] = useState(false);
  const [view, setView] = useState(!!referenceId);
  const [filter, setFilter] = useState(false);
  const [modalValues, setModalValues] = useState(null);
  const [amount, setAmount] = useState(addAmount);
  const [addAmountModal, setAddAmountModal] = useState(false);
  const [adjustModal, setAdjustModal] = useState(false);
  const [modalSupplier, setModalSupplier] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [printSingleProduct, setPrintSingleProduct] = useState(null);
  const [bulkPrintModal, setBulkPrintModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPrintItems, setBulkPrintItems] = useState(null);
  const [scaleExportModal, setScaleExportModal] = useState(false);
  const [scaleExportItems, setScaleExportItems] = useState(null);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [search, setSearch] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [clickRight, setClickRight] = useState(initialClick);
  const handleClickRight = (event, values) => {
    event.preventDefault();
    setModalValues({
      ...initialState,
      ...values,
      particulars: values.particulars,
      show_in_catalog: values.show_in_catalog === '1' || values.show_in_catalog === 1 || values.show_in_catalog === true,
      images: parseImages(values.images),
    });
    setClickRight({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };
  const handleCloseClickRight = () => {
    setClickRight(initialClick);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClickLeft = (event, values) => {
    setModalValues({
      ...initialState,
      ...values,
      particulars: values.particulars,
      show_in_catalog: values.show_in_catalog === '1' || values.show_in_catalog === 1 || values.show_in_catalog === true,
      images: parseImages(values.images),
    });
    setAnchorEl(event.currentTarget);
  };

  const handleCloseClickLeft = () => {
    setAnchorEl(null);
  };

  const [categoryLoad, categoryInfo] = useApi({
    method: 'get',
    url: '/products/categories',
  });

  const [suppliersLoad, suppliersInfo] = useApi({
    method: 'get',
    url: '/suppliers',
  });

  const [filterLoad, filterInfo] = useApi({
    debounceDelay: 500,
    method: 'get',
    url: '/products',
  });

  const [updateLoad, updateInfo, setUpdateInfo] = useApi({
    method: 'put',
    url: '/products',
    onCompleted: (response) => {
      if (response) {
        filterLoad({
          params: search,
        });
      }
    },
  });

  const [addAmountLoad, addAmountInfo, setAddAmountInfo] = useApi({
    method: 'patch',
    url: '/products',
    onCompleted: (response) => {
      if (response) {
        filterLoad({
          params: search,
        });
      }
    },
  });

  const [adjustLoad, adjustInfo, setAdjustInfo] = useApi({
    method: 'post',
    url: '/products/adjust',
    onCompleted: (response) => {
      if (response) {
        filterLoad({
          params: search,
        });
      }
    },
  });

  const currentPageItems = (filterInfo.data || []).slice(
    page * rowsPerPage,
    rowsPerPage === -1 ? undefined : page * rowsPerPage + rowsPerPage,
  );
  const isAllPageSelected =
    currentPageItems.length > 0 &&
    currentPageItems.every((p) => selectedIds.includes(p.id));
  const isSomePageSelected =
    currentPageItems.some((p) => selectedIds.includes(p.id)) && !isAllPageSelected;

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (!filterInfo.data) return;
    const allIds = filterInfo.data.map((p) => p.id);
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleToggleSelectPage = () => {
    const pageIds = currentPageItems.map((p) => p.id);
    if (isAllPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleBatchCatalogVisibility = (show) => {
    if (!filterInfo.data || selectedIds.length === 0) return;
    const selectedProducts = filterInfo.data.filter((p) =>
      selectedIds.includes(p.id),
    );
    const updatePayload = selectedProducts.map((product) => ({
      id: product.id,
      name: product.name,
      barCode: product.barCode,
      category: Number(product.category),
      particulars: product.particulars,
      value: product.value,
      supplier: product.supplier?.id || product.supplier,
      show_in_catalog: show,
    }));

    updateLoad({
      data: updatePayload,
      onCompleted: (response) => {
        if (response) {
          filterLoad({
            params: search,
          });
          setSelectedIds([]);
        }
      },
    });
    setAlert(true);
  };

  const handleBatchPrintLabels = () => {
    if (!filterInfo.data || selectedIds.length === 0) return;
    const selectedProducts = filterInfo.data.filter((p) =>
      selectedIds.includes(p.id),
    );
    setBulkPrintItems(selectedProducts);
    setBulkPrintModal(true);
  };

  const handleBatchScaleExport = () => {
    if (!filterInfo.data || selectedIds.length === 0) return;
    const selectedProducts = filterInfo.data.filter((p) =>
      selectedIds.includes(p.id),
    );
    setScaleExportItems(selectedProducts);
    setScaleExportModal(true);
  };

  useEffect(() => {
    if (referenceId) initialState.id = referenceId;
    const params = referenceId ? { id: referenceId } : '';
    filterLoad({
      params,
      onCompleted: (res) => {
        if (res && referenceId) {
          const [data] = res.data;
          setModalValues({
            ...initialState,
            ...data,
            particulars: data.particulars,
          });
        }
      },
    });
    suppliersLoad();
    categoryLoad({
      params: { isActive: 1 },
    });
  }, []);

  function handleModalFilter() {
    setFilter(!filter);
  }

  const [modalTab, setModalTab] = useState(0);
  const [stockSubTab, setStockSubTab] = useState(0);
  const [openFormatModal, setOpenFormatModal] = useState(false);
  const [editingFormatIndex, setEditingFormatIndex] = useState(null);
  const [editingFormatData, setEditingFormatData] = useState(null);
  const [adminAdjustState, setAdminAdjustState] = useState({
    mode: 'SAIDA',
    amount: 1,
    newTotal: 0,
    cost: 0,
    justification: '',
  });

  function handleModalAmount(values, isClick) {
    if (!addAmountModal && values) {
      setAmount({
        ...addAmount,
        id: values.id,
        cost: values.cost,
        costTotal: values.cost,
      });
    } else setAmount(addAmount);
    if (isClick) {
      setAmount({ ...addAmount, id: modalValues.id, cost: modalValues.cost });
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setAddAmountModal(!addAmountModal);
  }

  function handleModalUpdate(values, isClick, initialTab = 0) {
    if (values) {
      setModalValues({
        ...initialState,
        ...values,
        particulars: values.particulars,
        unit: values.unit ? normalizeUnitCode(values.unit) : 'UN',
        show_in_catalog: values.show_in_catalog === '1' || values.show_in_catalog === 1 || values.show_in_catalog === true,
        images: parseImages(values.images),
        childFormats: values.childFormats || [],
        parentProductId: values.parentProductId || null,
        fractionFactor: values.fractionFactor || null,
        parentName: values.parentName || '',
      });
      setAmount({
        ...addAmount,
        id: values.id,
        cost: values.cost || 0,
        costTotal: values.cost || 0,
      });
      setAdminAdjustState({
        mode: 'SAIDA',
        amount: 1,
        newTotal: Number(values.amount) || 0,
        cost: Number(values.cost) || 0,
        justification: '',
      });
    }
    setModalTab(initialTab);
    if (isClick) {
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setView(!view);
  }

  function handleExecuteAdjustment(productId) {
    const payload = {
      id: productId,
      justification: adminAdjustState.justification,
    };

    if (adminAdjustState.mode === 'CORRECAO') {
      const current = Number(modalValues?.amount) || 0;
      const target = Number(adminAdjustState.newTotal);
      const diff = target - current;
      if (diff === 0) return;

      payload.operation = diff > 0 ? 'ENTRADA' : 'SAIDA';
      payload.amount = Math.abs(diff);
      if (payload.operation === 'ENTRADA') {
        payload.cost = Number(adminAdjustState.cost);
      }
    } else if (adminAdjustState.mode === 'SAIDA') {
      payload.operation = 'SAIDA';
      payload.amount = Number(adminAdjustState.amount);
    } else {
      payload.operation = 'ENTRADA';
      payload.amount = Number(adminAdjustState.amount);
      payload.cost = Number(adminAdjustState.cost);
    }

    onAdjust(payload);
    handleModalUpdate();
    setModalValues(null);
  }

  function handleModalAdjust(values, isClick) {
    if (values) {
      setModalValues({
        ...initialState,
        ...values,
        particulars: values.particulars,
        unit: values.unit ? normalizeUnitCode(values.unit) : 'UN',
      });
    }
    if (isClick) {
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setAdjustModal(!adjustModal);
  }

  function closeAlert(event, reason) {
    setAddAmountInfo();
    setUpdateInfo();
    setAdjustInfo();
    if (reason === 'clickaway') {
      return;
    }
    setAlert(false);
  }

  function onSubmit(values) {
    const searchValues = {
      ...values,
      category: values.category ? Number(values.category.id) : '',
      supplier: values.supplier ? Number(values.supplier.id) : '',
    };
    if (values.unit) {
      searchValues.unit = values.unit;
    } else {
      delete searchValues.unit;
    }
    if (values.lacking) {
      searchValues.lacking = 1;
    } else {
      delete searchValues.lacking;
    }
    if (values.showInCatalog) {
      searchValues.show_in_catalog = 1;
    } else {
      delete searchValues.show_in_catalog;
    }
    delete searchValues.showInCatalog;
    filterLoad({
      debounced: true,
      params: emptyObject(searchValues),
    });
    setSearch(emptyObject(searchValues));
    setPage(0);
  }

  function onUpdate(values) {
    const updateValues = { id: values.id };

    if (!modalValues) {
      if (values.name) updateValues.name = values.name;
      if (values.description !== undefined) updateValues.description = values.description;
      if (values.barCode !== undefined) updateValues.barCode = values.barCode;
      if (values.category) updateValues.category = Number(values.category);
      if (values.unit) updateValues.unit = normalizeUnitCode(values.unit);
      if (values.particulars) updateValues.particulars = values.particulars;
      if (!values.parentProductId && values.cost !== undefined && values.cost !== '') {
        updateValues.cost = Number(values.cost);
      }
      if (values.value !== undefined && values.value !== '') updateValues.value = Number(values.value);
      if (values.supplier) updateValues.supplier = values.supplier?.id || values.supplier;
      if (values.show_in_catalog !== undefined) updateValues.show_in_catalog = Boolean(values.show_in_catalog);
      if (values.images) updateValues.images = values.images;
      if (values.childFormats) updateValues.childFormats = values.childFormats;
      if (values.parentProductId) {
        updateValues.parentProductId = values.parentProductId;
        updateValues.fractionFactor = values.fractionFactor;
      }
    } else {
      // Envia somente os campos que foram alterados pelo usuário
      if (values.name !== modalValues.name) {
        updateValues.name = values.name;
      }
      if ((values.description || '') !== (modalValues.description || '')) {
        updateValues.description = values.description;
      }
      if ((values.barCode || '') !== (modalValues.barCode || '')) {
        updateValues.barCode = values.barCode;
      }
      if (Number(values.category) !== Number(modalValues.category)) {
        updateValues.category = Number(values.category);
      }
      const newUnit = normalizeUnitCode(values.unit || 'UN');
      const oldUnit = normalizeUnitCode(modalValues.unit || 'UN');
      if (newUnit !== oldUnit) {
        updateValues.unit = newUnit;
      }
      if (JSON.stringify(values.particulars || {}) !== JSON.stringify(modalValues.particulars || {})) {
        updateValues.particulars = values.particulars;
      }
      // O custo de produto fracionado não deve ser enviado; para outros, apenas se mudou
      const isChild = Boolean(values.parentProductId || modalValues.parentProductId);
      if (!isChild) {
        const newCost = Number(values.cost);
        const oldCost = Number(modalValues.cost);
        if (!Number.isNaN(newCost) && Math.abs(newCost - oldCost) > 0.001) {
          updateValues.cost = newCost;
        }
      }
      const newValue = Number(values.value);
      const oldValue = Number(modalValues.value);
      if (!Number.isNaN(newValue) && Math.abs(newValue - oldValue) > 0.001) {
        updateValues.value = newValue;
      }
      const newSupplier = values.supplier?.id || values.supplier;
      const oldSupplier = modalValues.supplier?.id || modalValues.supplier;
      if (newSupplier && String(newSupplier) !== String(oldSupplier)) {
        updateValues.supplier = Number(newSupplier);
      }
      const newCatalog = Boolean(values.show_in_catalog);
      const oldCatalog = Boolean(modalValues.show_in_catalog);
      if (newCatalog !== oldCatalog) {
        updateValues.show_in_catalog = newCatalog;
      }
      if (JSON.stringify(values.images || []) !== JSON.stringify(modalValues.images || [])) {
        updateValues.images = values.images;
      }
      if (JSON.stringify(values.childFormats || []) !== JSON.stringify(modalValues.childFormats || [])) {
        updateValues.childFormats = values.childFormats;
      }
      if (values.fractionFactor && Number(values.fractionFactor) !== Number(modalValues.fractionFactor)) {
        updateValues.fractionFactor = Number(values.fractionFactor);
      }
    }

    if (Object.keys(updateValues).length <= 1) {
      setView(!view);
      setModalValues(null);
      return;
    }

    updateLoad({
      data: updateValues,
    });
    setView(!view);
    setAlert(true);
  }

  const toggleProductCatalog = (product) => {
    if (!product) return;
    const isCatalog = product.show_in_catalog === '1' || product.show_in_catalog === 1 || product.show_in_catalog === true;
    const updateValues = {
      id: product.id,
      name: product.name,
      barCode: product.barCode,
      category: Number(product.category),
      particulars: product.particulars,
      value: product.value,
      supplier: product.supplier?.id || product.supplier,
      show_in_catalog: !isCatalog,
    };
    updateLoad({
      data: updateValues,
      onCompleted: (response) => {
        if (response) {
          filterLoad({
            params: search,
          });
        }
      },
    });
    setAlert(true);
  };

  function onAddAmount() {
    const data = {
      id: amount.id,
      amount: amount.amount,
      cost: amount.cost,
    };
    addAmountLoad({
      data,
    });
    setAddAmountModal(!addAmountModal);
    setAlert(true);
  }

  function onAdjust(data) {
    adjustLoad({
      data,
    });
    setAdjustModal(false);
    setAlert(true);
  }

  return (
    <>
      <UIContainer container direction="row">
        <Grid
          item
          container
          direction="row"
          justify="space-between"
          alignItems="center"
          xs={12}
          style={{ marginBottom: 16 }}
        >
          <UIFormTitle title="Filtrar produtos" />
          <Box className={classes.headerActions}>
            <Button
              variant="contained"
              color="default"
              size={isXs ? 'small' : 'medium'}
              className={classes.headerActionBtn}
              startIcon={<PrintIcon />}
              onClick={() => setBulkPrintModal(true)}
            >
              {isXs ? 'Etiquetas' : 'Imprimir Etiquetas'}
            </Button>
            <Button
              variant="contained"
              color="default"
              size={isXs ? 'small' : 'medium'}
              className={classes.headerActionBtn}
              startIcon={<FitnessCenterIcon />}
              onClick={() => {
                setScaleExportItems(null);
                setScaleExportModal(true);
              }}
            >
              {isXs ? 'Balança' : 'Carga Balança'}
            </Button>
            <Button
              variant="contained"
              color="default"
              size={isXs ? 'small' : 'medium'}
              className={classes.headerActionBtn}
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportModal(true)}
            >
              {isXs ? 'XML' : 'Importar XML'}
            </Button>
          </Box>
        </Grid>
      </UIContainer>

      <Formik onSubmit={onSubmit} initialValues={initialState}>
        {({ values, submitForm, setFieldValue }) => (
          <Form onChange={() => submitForm()}>
            <UIContainer container direction="row">

              <Grid item xs={12}>
                <Paper className={classes.filterPaper} elevation={0} variant="outlined">
                  <Grid container spacing={2} alignItems="center">
                    <Hidden smDown>
                      <Grid item xs={12} md={1}>
                        <Field
                          name="id"
                          label="ID"
                          placeholder="ID"
                          variant="outlined"
                          size="small"
                          fullWidth
                          as={TextField}
                        />
                      </Grid>
                    </Hidden>
                    <Grid item xs={6} md={3}>
                      <Field
                        name="name"
                        label="Nome"
                        placeholder="Nome do produto"
                        variant="outlined"
                        size="small"
                        fullWidth
                        as={TextField}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Autocomplete
                        clearOnEscape
                        noOptionsText="Sem opções"
                        disabled={categoryInfo.loading}
                        options={categoryInfo.data !== null ? categoryInfo.data : []}
                        getOptionLabel={(category) => category.name}
                        onChange={(event, category) => {
                          setFieldValue('category', category);
                          submitForm();
                        }}
                        renderInput={(params) => (
                          <Field
                            {...params}
                            label="Categoria"
                            name="category"
                            variant="outlined"
                            size="small"
                            placeholder="Categoria"
                            component={TextField}
                          />
                        )}
                      />
                    </Grid>
                    <Hidden smDown>
                      <Grid item xs={12} md={2}>
                        <Field
                          name="barCode"
                          label="Código"
                          placeholder="Código de barras"
                          variant="outlined"
                          size="small"
                          fullWidth
                          as={TextField}
                        />
                      </Grid>
                    </Hidden>
                    <Hidden smDown>
                      <Grid item xs={12} md={2}>
                        <CurrencyTextField
                          type="tel"
                          value={values.value}
                          label="Valor de venda"
                          variant="outlined"
                          size="small"
                          decimalCharacter=","
                          digitGroupSeparator="."
                          currencySymbol="R$"
                          outputFormat="string"
                          onChange={(ev, value) => setFieldValue('value', value)}
                          fullWidth
                        />
                      </Grid>
                    </Hidden>
                    <Hidden smDown>
                      <Grid item xs={12} md={1}>
                        <UIButtonSearch xs={12} />
                      </Grid>
                    </Hidden>
                  </Grid>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ marginTop: 10 }}
                  >
                    <Hidden smDown>
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={Boolean(values.lacking)}
                              onChange={(ev) => {
                                ev.stopPropagation();
                                const checked = ev.target.checked;
                                setFieldValue('lacking', checked);
                                onSubmit({
                                  ...values,
                                  lacking: checked,
                                });
                              }}
                            />
                          }
                          label="Mostrar apenas produtos em falta"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={Boolean(values.showInCatalog)}
                              onChange={(ev) => {
                                ev.stopPropagation();
                                const checked = ev.target.checked;
                                setFieldValue('showInCatalog', checked);
                                onSubmit({
                                  ...values,
                                  showInCatalog: checked,
                                });
                              }}
                            />
                          }
                          label="Somente itens do catálogo"
                        />
                      </Box>
                    </Hidden>

                    <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ marginLeft: 'auto', padding: '4px 0' }}>
                      <UIFilter onClick={handleModalFilter} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </UIContainer>

            <UIModalFilter
              open={filter}
              changeModal={handleModalFilter}
              submit={submitForm}
            >
              <Grid container spacing={2}>
                <Hidden mdUp>
                  <Grid item xs={12}>
                    <Field
                      name="id"
                      label="ID"
                      placeholder="ID"
                      variant="outlined"
                      fullWidth
                      as={TextField}
                    />
                  </Grid>
                </Hidden>
                <Grid item xs={12}>
                  <Autocomplete
                    clearOnEscape
                    noOptionsText="Sem opções"
                    value={values.supplier}
                    disabled={suppliersInfo.loading}
                    options={
                      suppliersInfo.data !== null ? suppliersInfo.data : []
                    }
                    getOptionLabel={(supplier) => supplier.name}
                    onChange={(event, supplier) => {
                      setFieldValue('supplier', supplier);
                    }}
                    renderInput={(params) => (
                      <Field
                        {...params}
                        label="Fornecedor"
                        name="supplier"
                        variant="outlined"
                        placeholder="Fornecedor"
                        component={TextField}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="unit"
                    label="Unidade de Medida"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.unit || ''}
                    onChange={(e) => setFieldValue('unit', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas as unidades</em>
                    </MenuItem>
                    {PRODUCT_UNITS.map((u) => (
                      <MenuItem key={u.code} value={u.code}>
                        {u.code} - {u.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <CurrencyTextField
                    decimalPlaces={5}
                    value={values.cost}
                    label="Custo"
                    variant="outlined"
                    decimalCharacter=","
                    digitGroupSeparator="."
                    currencySymbol="R$"
                    outputFormat="string"
                    onChange={(ev, value) => setFieldValue('cost', value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="particulars"
                    label="Característica"
                    variant="outlined"
                    placeholder="Característica"
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="amount"
                    label="Quantidade"
                    type="number"
                    variant="outlined"
                    placeholder="Quantidade"
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Hidden mdUp>
                  <Grid item xs={12}>
                    <Field
                      name="barCode"
                      label="Código"
                      variant="outlined"
                      placeholder="Código de barras"
                      fullWidth
                      as={TextField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CurrencyTextField
                      type="tel"
                      value={values.value}
                      label="Valor de venda"
                      variant="outlined"
                      decimalCharacter=","
                      digitGroupSeparator="."
                      currencySymbol="R$"
                      outputFormat="string"
                      onChange={(ev, value) => setFieldValue('value', value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.lacking}
                          onChange={(ev) =>
                            setFieldValue('lacking', ev.target.checked)
                          }
                        />
                      }
                      label="Em falta"
                    />
                  </Grid>
                </Hidden>
              </Grid>
            </UIModalFilter>
          </Form>
        )}
      </Formik>
      <UIContainer>
        <Grid item xs={12}>
          <UIFormTitle title="Lista de produtos" />
        </Grid>
        <Grid item xs={12}>
          <Hidden smDown>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell padding="checkbox" className={classes.tableHeadCell}>
                      <Checkbox
                        indeterminate={isSomePageSelected}
                        checked={isAllPageSelected}
                        onChange={handleToggleSelectPage}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>ID</TableCell>
                    <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                    <TableCell className={classes.tableHeadCell}>Categoria</TableCell>
                    <TableCell className={classes.tableHeadCell}>Custo</TableCell>
                    <TableCell className={classes.tableHeadCell}>Venda</TableCell>
                    <TableCell className={classes.tableHeadCell}>Markup</TableCell>
                    <TableCell className={classes.tableHeadCell}>Margem</TableCell>
                    <TableCell className={classes.tableHeadCell}>Lucro</TableCell>
                    <TableCell className={classes.tableHeadCell}>Qntd.</TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filterInfo.loading && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {filterInfo.data &&
                    categoryInfo.data &&
                    filterInfo.data
                      .slice(
                        page * rowsPerPage,
                        rowsPerPage === -1
                          ? undefined
                          : page * rowsPerPage + rowsPerPage,
                      )
                      .map((data) => (
                        <TableRow
                          onDoubleClick={() => handleModalUpdate(data)}
                          onContextMenu={
                            !mobile
                              ? (ev) => handleClickRight(ev, data)
                              : undefined
                          }
                          onClick={
                            mobile ? (ev) => handleClickLeft(ev, data) : undefined
                          }
                          hover
                          key={data.id}
                          className={data.lacking === '1' ? classes.rowLacking : classes.rowNormal}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.includes(data.id)}
                              onChange={() => handleToggleSelect(data.id)}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {data.id}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {data.name}
                            </Typography>
                            {data.parentProductId && (
                              <Chip
                                size="small"
                                label={data.parentName ? ` Fracionado de: ${data.parentName}` : ' Fracionado'}
                                style={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  marginTop: 4,
                                  marginRight: 4,
                                  backgroundColor: '#e3f2fd',
                                  color: '#0d47a1',
                                  fontWeight: 500,
                                }}
                              />
                            )}
                            {data.childFormats && data.childFormats.length > 0 && (
                              <Chip
                                size="small"
                                label={`${data.childFormats.length} formato(s)`}
                                style={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  marginTop: 4,
                                  marginRight: 4,
                                  backgroundColor: '#f1f5f9',
                                  color: '#475569',
                                  fontWeight: 500,
                                }}
                              />
                            )}
                            {data.lacking === '1' && (
                              <Chip size="small" label="Em falta" color="error" variant="outlined" style={{ height: 20, fontSize: '0.7rem', marginTop: 4 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={categoryInfo.data.find((item) => item.id === data.category)?.name}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formattedCurrency(data.cost)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {formattedCurrency(data.value)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {calculateMarkupByValue(data.cost, data.value)} %
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              className={
                                parseFloat(calculateMarginByValue(data.cost, data.value)) > 20
                                  ? classes.marginHigh
                                  : classes.marginLow
                              }
                            >
                              {calculateMarginByValue(data.cost, data.value)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {formattedCurrency(calculateProfitByValue(data.cost, data.value))}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {data.parentProductId ? (
                                <Tooltip title={`Estoque dinâmico proporcional ao produto pai (${formatStockAmount(data.amount)} ${data.unit || 'UN'})`}>
                                  <span style={{ color: '#0d47a1', fontWeight: 600, cursor: 'help' }}>
                                    {formatStockAmount(data.amount)}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span>{formatStockAmount(data.amount)}</span>
                              )}
                              <Chip
                                size="small"
                                label={data.unit || 'UN'}
                                style={{
                                  height: 18,
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  backgroundColor: data.parentProductId ? '#e0f2fe' : '#f1f5f9',
                                  color: data.parentProductId ? '#0284c7' : '#475569',
                                  marginLeft: 4,
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" justifyContent="flex-end">
                              <Tooltip title={data.show_in_catalog === '1' || data.show_in_catalog === 1 || data.show_in_catalog === true ? "Remover do Catálogo Virtual" : "Exibir no Catálogo Virtual"}>
                                <IconButton onClick={() => toggleProductCatalog(data)} size="small" className={classes.actionBtn}>
                                  {data.show_in_catalog === '1' || data.show_in_catalog === 1 || data.show_in_catalog === true ? (
                                    <VisibilityIcon fontSize="small" style={{ color: '#17497F' }} />
                                  ) : (
                                    <VisibilityOffIcon fontSize="small" style={{ color: '#9E9E9E' }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar Produto">
                                <IconButton onClick={() => handleModalUpdate(data)} size="small" className={classes.actionBtn}>
                                  <EditIcon fontSize="small" style={{ color: '#17497F' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Imprimir Etiqueta">
                                <IconButton onClick={() => setPrintSingleProduct(data)} size="small" className={classes.actionBtn}>
                                  <PrintIcon fontSize="small" style={{ color: '#17497F' }} />
                                </IconButton>
                              </Tooltip>
                              {!data.parentProductId && (
                                <Tooltip title="Entrada rápida de estoque">
                                  <IconButton onClick={() => handleModalAmount(data)} size="small" className={classes.actionBtn}>
                                    <AddCircleIcon fontSize="small" style={{ color: '#17497F' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  {filterInfo.error && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">Sem resultados encontrados</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <UITablePagination
                count={filterInfo.data ? filterInfo.data.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </TableContainer>
          </Hidden>

          {/* Cards Mobile */}
          <Hidden mdUp>
            <Box style={{ width: '100%' }}>
              {filterInfo.data && filterInfo.data.length > 0 && (
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} px={0.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        indeterminate={isSomePageSelected}
                        checked={isAllPageSelected}
                        onChange={handleToggleSelectPage}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        Selecionar página
                      </Typography>
                    }
                  />
                </Box>
              )}
              {filterInfo.loading && (
                <CircularProgress style={{ margin: '16px auto', display: 'block' }} />
              )}
              {filterInfo.data &&
                categoryInfo.data &&
                filterInfo.data
                  .slice(
                    page * rowsPerPage,
                    rowsPerPage === -1
                      ? undefined
                      : page * rowsPerPage + rowsPerPage,
                  )
                  .map((data) => {
                    const isLacking = data.lacking === '1';
                    const categoryName = categoryInfo.data.find((item) => item.id === data.category)?.name;
                    const marginVal = parseFloat(calculateMarginByValue(data.cost, data.value));

                    return (
                      <Card
                        key={data.id}
                        variant="outlined"
                        style={{
                          marginBottom: 12,
                          borderLeft: `4px solid ${isLacking ? '#D32F2F' : '#17497F'}`,
                          borderRadius: 8,
                          backgroundColor: isLacking ? '#FFF8F8' : '#FFFFFF',
                        }}
                      >
                        <CardContent style={{ paddingBottom: 8 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                              <Checkbox
                                checked={selectedIds.includes(data.id)}
                                onChange={() => handleToggleSelect(data.id)}
                                color="primary"
                                size="small"
                                style={{ padding: 4 }}
                              />
                              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                #{data.id} - {data.name}
                              </Typography>
                            </Box>
                            <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
                              {categoryName && (
                                <Chip
                                  label={categoryName}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {isLacking && (
                                <Chip
                                  label="Em falta"
                                  size="small"
                                  color="error"
                                  style={{ fontWeight: 600 }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Divider style={{ margin: '8px 0' }} />
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Preço de Venda
                              </Typography>
                              <Typography variant="body1" style={{ fontWeight: 'bold', color: '#17497F' }}>
                                {formattedCurrency(data.value)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Estoque Atual
                              </Typography>
                              <Typography variant="body1" style={{ fontWeight: 'bold', color: isLacking ? '#D32F2F' : 'inherit' }}>
                                {formatStockAmount(data.amount)} {data.unit || 'UN'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Custo
                              </Typography>
                              <Typography variant="body2">
                                {formattedCurrency(data.cost)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Margem
                              </Typography>
                              <Typography
                                variant="body2"
                                className={marginVal > 20 ? classes.marginHigh : classes.marginLow}
                              >
                                {calculateMarginByValue(data.cost, data.value)} %
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Lucro
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {formattedCurrency(calculateProfitByValue(data.cost, data.value))}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions
                          style={{
                            justifyContent: 'flex-end',
                            paddingTop: 0,
                            paddingRight: 16,
                            paddingBottom: 8,
                          }}
                        >
                          <Tooltip title={data.show_in_catalog === '1' || data.show_in_catalog === 1 || data.show_in_catalog === true ? "Remover do Catálogo Virtual" : "Exibir no Catálogo Virtual"}>
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={() => toggleProductCatalog(data)}
                            >
                              {data.show_in_catalog === '1' || data.show_in_catalog === 1 || data.show_in_catalog === true ? (
                                <VisibilityIcon fontSize="small" style={{ color: '#17497F' }} />
                              ) : (
                                <VisibilityOffIcon fontSize="small" style={{ color: '#9E9E9E' }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar Produto">
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={() => handleModalUpdate(data)}
                            >
                              <EditIcon fontSize="small" style={{ color: '#17497F' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir Etiqueta">
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={() => setPrintSingleProduct(data)}
                            >
                              <PrintIcon fontSize="small" style={{ color: '#17497F' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Entrada rápida de estoque">
                            <IconButton
                              size="small"
                              className={classes.actionBtn}
                              onClick={() => handleModalAmount(data)}
                            >
                              <AddCircleIcon fontSize="small" style={{ color: '#17497F' }} />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    );
                  })}
              {filterInfo.error && (
                <Typography color="textSecondary" align="center" style={{ padding: '16px 0' }}>
                  Sem resultados encontrados
                </Typography>
              )}
              <UITablePagination
                count={filterInfo.data ? filterInfo.data.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Box>
          </Hidden>

          <Menu
            keepMounted
            open={clickRight.mouseY !== null}
            onClose={handleCloseClickRight}
            anchorReference="anchorPosition"
            anchorPosition={
              clickRight.mouseY !== null && clickRight.mouseX !== null
                ? {
                  top: clickRight.mouseY,
                  left: clickRight.mouseX,
                }
                : undefined
            }
          >
            <MenuItem onClick={() => { toggleProductCatalog(modalValues); handleCloseClickRight(); }}>
              {modalValues?.show_in_catalog ? (
                <><VisibilityOffIcon fontSize="small" style={{ marginRight: 10 }} /> Remover do Catálogo</>
              ) : (
                <><VisibilityIcon fontSize="small" style={{ marginRight: 10 }} /> Exibir no Catálogo</>
              )}
            </MenuItem>
            {!modalValues?.parentProductId && (
              <MenuItem onClick={() => handleModalAmount(modalValues, true)}>
                <AddCircleIcon fontSize="small" style={{ marginRight: 10 }} /> Adicionar estoque rápido
              </MenuItem>
            )}
            <MenuItem onClick={() => handleModalUpdate(null, true, 0)}>
              <DescriptionIcon fontSize="small" style={{ marginRight: 10 }} /> Dados do Produto
            </MenuItem>
            <MenuItem onClick={() => handleModalUpdate(null, true, 1)}>
              <MonetizationOnIcon fontSize="small" style={{ marginRight: 10 }} /> Preços & Margens
            </MenuItem>
            <MenuItem onClick={() => handleModalUpdate(null, true, 2)}>
              <StorageIcon fontSize="small" style={{ marginRight: 10 }} /> Estoque & Ajustes
            </MenuItem>
            {!modalValues?.parentProductId && (
              <MenuItem onClick={() => handleModalUpdate(null, true, 3)}>
                <CallSplitIcon fontSize="small" style={{ marginRight: 10 }} /> Formatos de Venda
              </MenuItem>
            )}
          </Menu>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => handleCloseClickLeft()}
          >
            <MenuItem onClick={() => { toggleProductCatalog(modalValues); handleCloseClickLeft(); }}>
              {modalValues?.show_in_catalog ? (
                <><VisibilityOffIcon fontSize="small" style={{ marginRight: 10 }} /> Remover do Catálogo</>
              ) : (
                <><VisibilityIcon fontSize="small" style={{ marginRight: 10 }} /> Exibir no Catálogo</>
              )}
            </MenuItem>
            {!modalValues?.parentProductId && (
              <MenuItem onClick={() => handleModalAmount(modalValues, true)}>
                <AddCircleIcon fontSize="small" style={{ marginRight: 10 }} /> Adicionar estoque rápido
              </MenuItem>
            )}
            <MenuItem onClick={() => handleModalUpdate(null, true, 0)}>
              <DescriptionIcon fontSize="small" style={{ marginRight: 10 }} /> Dados do Produto
            </MenuItem>
            <MenuItem onClick={() => handleModalUpdate(null, true, 1)}>
              <MonetizationOnIcon fontSize="small" style={{ marginRight: 10 }} /> Preços & Margens
            </MenuItem>
            <MenuItem onClick={() => handleModalUpdate(null, true, 2)}>
              <StorageIcon fontSize="small" style={{ marginRight: 10 }} /> Estoque & Ajustes
            </MenuItem>
            {!modalValues?.parentProductId && (
              <MenuItem onClick={() => handleModalUpdate(null, true, 3)}>
                <CallSplitIcon fontSize="small" style={{ marginRight: 10 }} /> Formatos de Venda
              </MenuItem>
            )}
          </Menu>
          {filterInfo.error && (
            <TableRow>
              <TableCell colSpan={10} align="center">Sem resultados encontrados</TableCell>
            </TableRow>
          )}
          <Dialog
            open={addAmountModal}
            onClose={() => handleModalAmount()}
            fullWidth
            maxWidth="sm"
          >
            <UIDialogTitle
              id="form-dialog-title"
              onClose={() => handleModalAmount()}
            >
              Adicionar quantidade
            </UIDialogTitle>
            <DialogContent>
              <Grid
                container
                spacing={2}
                alignItems="center"
              >
                <Grid item xs={12} md={4}>
                  <TextField
                    name="amount"
                    label="Quantidade"
                    variant="outlined"
                    size="small"
                    value={amount.amount}
                    onChange={(ev) => {
                      if (ev.target.value > 0) {
                        setAmount({
                          ...amount,
                          amount: ev.target.value,
                          costTotal: ev.target.value * amount.cost,
                        });
                      }
                    }}
                    type="number"
                    error={!Number.isInteger(Number(amount.amount))}
                    helperText={
                      !Number.isInteger(Number(amount.amount)) &&
                      'Digite um inteiro'
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CurrencyTextField
                    type="tel"
                    value={amount.cost}
                    label="Custo unitário"
                    variant="outlined"
                    size="small"
                    decimalCharacter=","
                    digitGroupSeparator="."
                    currencySymbol="R$"
                    outputFormat="string"
                    onChange={(ev, value) => {
                      if (value >= 0) {
                        setAmount({
                          ...amount,
                          cost: value,
                          costTotal: value * amount.amount,
                        });
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CurrencyTextField
                    type="tel"
                    value={amount.costTotal}
                    label="Custo Total"
                    variant="outlined"
                    size="small"
                    decimalCharacter=","
                    digitGroupSeparator="."
                    currencySymbol="R$"
                    outputFormat="string"
                    onChange={(ev, value) => {
                      if (value >= 0) {
                        setAmount({
                          ...amount,
                          costTotal: value,
                          cost: value / amount.amount,
                        });
                      }
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleModalAmount()}>
                Cancelar
              </Button>
              <Button
                onClick={() => onAddAmount()}
                color="primary"
                variant="contained"
                disableElevation
              >
                Adicionar
              </Button>
            </DialogActions>
          </Dialog>
          <AdministrativeAdjustmentModal
            open={adjustModal}
            handleModal={handleModalAdjust}
            item={modalValues}
            type="ESTOQUE"
            onSubmit={onAdjust}
          />
        </Grid>
      </UIContainer>
      {modalValues && categoryInfo.data && suppliersInfo.data && (
        <Dialog
          open={view}
          onClose={() => {
            handleModalUpdate();
            setModalValues(null);
          }}
          aria-labelledby="form-dialog-title"
          maxWidth="md"
          fullWidth
          fullScreen={mobile}
          classes={{ paper: classes.dialogPaper }}
        >
          <Formik
            enableReinitialize
            onSubmit={onUpdate}
            initialValues={modalValues}
            validationSchema={validationSchema}
          >
            {({ values, errors, touched, setFieldValue, submitForm }) => {
              const numCost = Number(values.cost) || 0;
              const numValue = Number(values.value) || 0;
              const currentProfit = calculateProfitByValue(numCost, numValue);
              const currentMargin = parseFloat(calculateMarginByValue(numCost, numValue)) || 0;
              const currentMarkup = parseFloat(calculateMarkupByValue(numCost, numValue)) || 0;
              const isProfitPositive = currentProfit >= 0;

              return (
                <Form>
                  <UIDialogTitle
                    id="form-dialog-title"
                    onClose={() => {
                      handleModalUpdate();
                      setModalValues(null);
                    }}
                  >
                    Gerenciar Produto: {modalValues.name}
                  </UIDialogTitle>

                  <Box borderBottom={1} borderColor="divider" bgcolor="#fbfcfd">
                    <Tabs
                      value={modalTab}
                      onChange={(e, val) => setModalTab(val)}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab
                        className={classes.tabRoot}
                        icon={<DescriptionIcon fontSize="small" />}
                        label="Dados"
                      />
                      <Tab
                        className={classes.tabRoot}
                        icon={<MonetizationOnIcon fontSize="small" />}
                        label="Preços & Margens"
                      />
                      <Tab
                        className={classes.tabRoot}
                        icon={<StorageIcon fontSize="small" />}
                        label="Estoque & Ajustes"
                      />
                      {!values.parentProductId && (
                        <Tab
                          className={classes.tabRoot}
                          icon={<CallSplitIcon fontSize="small" />}
                          label={`Formatos (${(values.childFormats || []).length})`}
                        />
                      )}
                    </Tabs>
                  </Box>

                  <DialogContent className={classes.dialogContentStable}>
                    {/* ABA 0: DADOS DO PRODUTO */}
                    {modalTab === 0 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Field
                            name="name"
                            label="Nome do Produto"
                            error={Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            size="small"
                            fullWidth
                            as={TextField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            disableClearable
                            noOptionsText="Sem opções"
                            value={
                              (categoryInfo.data &&
                                categoryInfo.data.find(
                                  (item) => item.id == values.category,
                                )) ||
                              null
                            }
                            disabled={categoryInfo.loading}
                            options={
                              categoryInfo.data !== null ? categoryInfo.data : []
                            }
                            getOptionLabel={(category) => category.name}
                            getOptionSelected={(option, value) => option.id == value.id}
                            onChange={(event, category) => {
                              setFieldValue('category', category && category.id);
                              if (event && event.target.value !== '' && category && category.particulars) {
                                setFieldValue(
                                  'particulars',
                                  clearObject(category.particulars, true),
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <Field
                                {...params}
                                label="Categoria"
                                name="category"
                                variant="outlined"
                                size="small"
                                component={TextField}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: '#333' }}>
                              Descrição para o Catálogo Virtual
                            </Typography>
                            <UIDuxiaDescriptionButton
                              itemName={values.name}
                              categoryName={
                                categoryInfo.data &&
                                categoryInfo.data.find((item) => item.id == values.category)?.name
                              }
                              itemType="produto"
                              currentDescription={values.description}
                              onGenerated={(newDesc) => setFieldValue('description', newDesc)}
                            />
                          </Box>
                          <Field
                            name="description"
                            placeholder="Descrição detalhada do produto..."
                            variant="outlined"
                            size="small"
                            multiline
                            rows={3}
                            fullWidth
                            as={TextField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            disableClearable
                            noOptionsText="Sem opções"
                            value={
                              (suppliersInfo.data &&
                                suppliersInfo.data.find(
                                  (item) => item.id == values.supplier,
                                )) ||
                              null
                            }
                            disabled={suppliersInfo.loading}
                            options={
                              suppliersInfo.data !== null ? suppliersInfo.data : []
                            }
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option;
                              }
                              if (option.inputValue) {
                                return option.name;
                              }
                              return option.name;
                            }}
                            getOptionSelected={(option, value) => option.id === value.id}
                            filterOptions={(options, params) => {
                              const filtered = supplierFilter(options, params);
                              const existing = options.some(
                                (option) =>
                                  option.name.toLowerCase() ===
                                  params.inputValue.toLowerCase(),
                              );
                              if (params.inputValue !== '' && !existing) {
                                filtered.push({
                                  inputValue: params.inputValue,
                                  name: `Adicionar "${params.inputValue}"`,
                                });
                              }
                              return filtered;
                            }}
                            onChange={(event, newValue) => {
                              if (!newValue) {
                                setFieldValue('supplier', null);
                                return;
                              }
                              if (newValue.inputValue) {
                                setNewSupplierName(newValue.inputValue);
                                setModalSupplier(true);
                              } else {
                                setFieldValue('supplier', newValue.id);
                              }
                            }}
                            renderInput={(params) => (
                              <Field
                                {...params}
                                label="Fornecedor"
                                name="supplier"
                                variant="outlined"
                                size="small"
                                component={TextField}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            name="barCode"
                            label="Código de barras"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <UIBarcodeRegenerateButton
                                    itemId={values.id}
                                    itemType="product"
                                    currentBarCode={values.barCode}
                                    onGenerated={(newBarCode) => {
                                      setFieldValue('barCode', newBarCode);
                                      if (modalValues) {
                                        modalValues.barCode = newBarCode;
                                      }
                                      filterLoad({ params: search });
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            error={Boolean(errors.barCode)}
                            helperText={touched.barCode && errors.barCode}
                            variant="outlined"
                            size="small"
                            fullWidth
                            as={TextField}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(values.show_in_catalog)}
                                onChange={(e) => setFieldValue('show_in_catalog', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Exibir no Catálogo Virtual"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <UIImageUpload
                            images={values.images || []}
                            onChange={(newImages) => setFieldValue('images', newImages)}
                            label="Imagens do Produto"
                            itemName={values.name}
                          />
                        </Grid>

                        {categoryInfo.data &&
                          (() => {
                            const selectedCat = categoryInfo.data.find(
                              (item) => String(item.id) === String(values.category),
                            );
                            if (!selectedCat || !selectedCat.particulars) return null;
                            let partsObj = selectedCat.particulars;
                            if (typeof partsObj === 'string') {
                              try {
                                partsObj = JSON.parse(partsObj);
                              } catch (e) {
                                partsObj = null;
                              }
                            }
                            if (!partsObj || typeof partsObj !== 'object' || Object.keys(partsObj).length === 0) return null;

                            return (
                              <Grid item xs={12}>
                                <Box
                                  style={{
                                    backgroundColor: '#f8fbff',
                                    border: '1px solid #d4e5f9',
                                    borderRadius: 8,
                                    padding: '14px',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    style={{
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5,
                                      marginBottom: 10,
                                      display: 'block',
                                      color: '#17497F',
                                    }}
                                  >
                                    Características da Categoria ({selectedCat.name})
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {Object.entries(partsObj).map(([key, value]) => (
                                      <Grid key={key} item xs={12} sm={6}>
                                        <FormControl variant="outlined" size="small" fullWidth>
                                          <InputLabel id={`label-particular-${key}`}>{capitalize(key)}</InputLabel>
                                          <Field
                                            name={`particulars.${key}`}
                                            disabled={!values.category}
                                            labelId={`label-particular-${key}`}
                                            label={capitalize(key)}
                                            as={Select}
                                          >
                                            <MenuItem value="">
                                              <em>Selecione o(a) {key}</em>
                                            </MenuItem>
                                            {Array.isArray(value)
                                              ? value.map((vl) => (
                                                <MenuItem key={vl} value={vl}>
                                                  {vl}
                                                </MenuItem>
                                              ))
                                              : value && (
                                                <MenuItem key={value} value={value}>
                                                  {value}
                                                </MenuItem>
                                              )}
                                          </Field>
                                        </FormControl>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              </Grid>
                            );
                          })()}
                      </Grid>
                    )}

                    {/* ABA 1: PREÇOS & MARGENS */}
                    {modalTab === 1 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Paper className={classes.metricCard} elevation={0}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Preço de Venda
                                </Typography>
                                <Typography variant="h6" style={{ fontWeight: 'bold', color: '#17497F' }}>
                                  {formattedCurrency(numValue)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Margem de Lucro
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  className={currentMargin > 20 ? classes.marginHigh : classes.marginLow}
                                >
                                  {currentMargin.toFixed(2)} %
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Lucro Estimado ({values.unit || 'UN'})
                                </Typography>
                                <span className={isProfitPositive ? classes.profitBadgePositive : classes.profitBadgeNegative}>
                                  {formattedCurrency(currentProfit)}
                                </span>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <CurrencyTextField
                            type="tel"
                            value={values.cost}
                            label={`Custo por ${values.unit || 'UN'} (R$)`}
                            variant={values.parentProductId ? 'filled' : 'outlined'}
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            error={Boolean(errors.cost)}
                            helperText={
                              values.parentProductId
                                ? ' Calculado automaticamente pelo produto pai'
                                : (touched.cost && errors.cost)
                            }
                            onChange={(ev, value) => setFieldValue('cost', value)}
                            fullWidth
                            disabled={Boolean(values.parentProductId)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <CurrencyTextField
                            type="tel"
                            value={values.value}
                            label={`Preço por ${values.unit || 'UN'} (R$)`}
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            error={Boolean(errors.value)}
                            helperText={touched.value && errors.value}
                            onChange={(ev, value) => setFieldValue('value', value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <CurrencyTextField
                            type="tel"
                            value={calculateMarkupByValue(values.cost, values.value)}
                            label="Markup %"
                            variant="filled"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="%"
                            outputFormat="string"
                            fullWidth
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <CurrencyTextField
                            type="tel"
                            value={calculateMarginByValue(values.cost, values.value)}
                            label="Margem %"
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="%"
                            outputFormat="string"
                            onBlur={(ev, value) => {
                              if (value !== undefined && value !== '') {
                                setFieldValue('margin', value);
                                const newVal = calculateValueByMargin(values.cost, value);
                                if (newVal) setFieldValue('value', newVal);
                              }
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <CurrencyTextField
                            type="tel"
                            value={calculateProfitByValue(values.cost, values.value)}
                            label="Lucro (R$)"
                            variant="filled"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            fullWidth
                            disabled
                          />
                        </Grid>
                      </Grid>
                    )}

                    {/* ABA 2: ESTOQUE & AJUSTES */}
                    {modalTab === 2 && (
                      values.parentProductId ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Paper className={classes.metricCard} elevation={0} style={{ marginBottom: 0 }}>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid #e2e8f0' }}
                              >
                                <Box>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Saldo em Estoque (Dinâmico)
                                  </Typography>
                                  <Typography variant="h5" style={{ fontWeight: 'bold', color: values.amount <= 0 ? '#D32F2F' : '#17497F' }}>
                                    {formatStockAmount(values.amount)} {values.unit || 'UN'}
                                  </Typography>
                                </Box>
                                <Chip label="Estoque Vinculado ao Pai" style={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 'bold' }} />
                              </Box>
                              <ProductUnitSelect
                                value={values.unit || 'UN'}
                                onChange={(newUnit) => setFieldValue('unit', newUnit)}
                                label="Unidade de Venda deste Formato"
                              />
                            </Paper>
                          </Grid>
                          <Grid item xs={12}>
                            <Paper style={{ padding: 20, backgroundColor: '#f0f7ff', border: '1px solid #bbdefb', borderRadius: 8 }}>
                              <Typography variant="subtitle1" style={{ color: '#0d47a1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span></span> Formato Fracionado Vinculado
                              </Typography>
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: 10, lineHeight: 1.6 }}>
                                Este produto é uma fração/unidade de venda derivada de <strong>{values.parentName || `Produto #${values.parentProductId}`}</strong>.
                                <br />
                                <strong>Rendimento:</strong> {formatStockAmount(values.fractionFactor || 1)} un. por unidade/fardo do produto pai.
                                <br />
                                <strong>Custo Proporcional:</strong> {formattedCurrency(values.cost)}.
                              </Typography>
                              <Typography variant="body2" style={{ marginTop: 12, color: '#1565c0', fontWeight: 500 }}>
                                Toda venda realizada neste formato abate proporcionalmente da embalagem principal. Para adicionar ou corrigir estoque físico, altere diretamente o produto principal.
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Paper className={classes.metricCard} elevation={0} style={{ marginBottom: 0 }}>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid #e2e8f0' }}
                              >
                                <Box>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Saldo em Estoque
                                  </Typography>
                                  <Typography variant="h5" style={{ fontWeight: 'bold', color: values.lacking === '1' || values.amount <= 0 ? '#D32F2F' : '#17497F' }}>
                                    {formatStockAmount(values.amount)} {values.unit || 'UN'}
                                  </Typography>
                                </Box>
                                {values.lacking === '1' || values.amount <= 0 ? (
                                  <Chip label="Em falta" color="error" variant="outlined" style={{ fontWeight: 'bold' }} />
                                ) : (
                                  <Chip label="Disponível" style={{ backgroundColor: '#e6f4ea', color: '#137333', fontWeight: 'bold' }} />
                                )}
                              </Box>
                              <ProductUnitSelect
                                value={values.unit || 'UN'}
                                onChange={(newUnit) => setFieldValue('unit', newUnit)}
                                label="Unidade de Medida de Estoque"
                              />
                            </Paper>
                          </Grid>

                          <Grid item xs={12}>
                            <Paper className={classes.subSectionCard} elevation={0} style={{ backgroundColor: '#ffffff', marginTop: 0 }}>
                              <Box className={classes.segmentedContainer}>
                                <ButtonBase
                                  className={stockSubTab === 0 ? classes.segmentedButtonActive : classes.segmentedButton}
                                  onClick={() => setStockSubTab(0)}
                                >
                                  <AddCircleIcon fontSize="small" style={{ color: stockSubTab === 0 ? '#17497F' : '#64748b', marginRight: 6 }} />
                                  Entrada Rápida
                                </ButtonBase>
                                <ButtonBase
                                  className={stockSubTab === 1 ? classes.segmentedButtonActive : classes.segmentedButton}
                                  onClick={() => setStockSubTab(1)}
                                >
                                  <SettingsIcon fontSize="small" style={{ color: stockSubTab === 1 ? '#17497F' : '#64748b', marginRight: 6 }} />
                                  Ajuste Administrativo
                                </ButtonBase>
                              </Box>

                              {stockSubTab === 0 ? (
                                <Box>
                                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                                    Adicionar itens ao estoque (Entrada)
                                  </Typography>
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        label="Quantidade"
                                        type="number"
                                        variant="outlined"
                                        size="small"
                                        value={amount.amount}
                                        onChange={(ev) => {
                                          const val = ev.target.value;
                                          if (val > 0) {
                                            setAmount({
                                              ...amount,
                                              amount: val,
                                              costTotal: val * amount.cost,
                                            });
                                          }
                                        }}
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <CurrencyTextField
                                        type="tel"
                                        value={amount.cost}
                                        label="Custo Unitário"
                                        variant="outlined"
                                        size="small"
                                        decimalCharacter=","
                                        digitGroupSeparator="."
                                        currencySymbol="R$"
                                        outputFormat="string"
                                        onChange={(ev, val) => {
                                          if (val >= 0) {
                                            setAmount({
                                              ...amount,
                                              cost: val,
                                              costTotal: val * amount.amount,
                                            });
                                          }
                                        }}
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <CurrencyTextField
                                        type="tel"
                                        value={amount.costTotal}
                                        label="Custo Total"
                                        variant="outlined"
                                        size="small"
                                        decimalCharacter=","
                                        digitGroupSeparator="."
                                        currencySymbol="R$"
                                        outputFormat="string"
                                        onChange={(ev, val) => {
                                          if (val >= 0) {
                                            setAmount({
                                              ...amount,
                                              costTotal: val,
                                              cost: val / (amount.amount || 1),
                                            });
                                          }
                                        }}
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box display="flex" justifyContent="flex-end" mt={1}>
                                        <Button
                                          onClick={() => {
                                            onAddAmount();
                                            handleModalUpdate();
                                            setModalValues(null);
                                          }}
                                          color="primary"
                                          variant="outlined"
                                          startIcon={<AddCircleIcon />}
                                          disabled={!amount.amount || Number(amount.amount) <= 0}
                                        >
                                          Registrar Entrada de Estoque
                                        </Button>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ) : (
                                <Box>
                                  <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e293b', marginBottom: 12 }}>
                                    Ajuste Administrativo de Estoque (Auditoria)
                                  </Typography>

                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        label="Tipo de Ajuste"
                                        select
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        value={adminAdjustState.mode}
                                        onChange={(e) => setAdminAdjustState({ ...adminAdjustState, mode: e.target.value })}
                                      >
                                        <MenuItem value="SAIDA">Baixa / Descarte / Avaria (Saída -)</MenuItem>
                                        <MenuItem value="CORRECAO">Correção de Inventário (Novo Saldo Real)</MenuItem>
                                        <MenuItem value="ENTRADA">Sobra de Estoque / Acréscimo (+)</MenuItem>
                                      </TextField>
                                    </Grid>

                                    {adminAdjustState.mode === 'CORRECAO' ? (
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          label="Nova Quantidade Total"
                                          type="number"
                                          size="small"
                                          variant="outlined"
                                          fullWidth
                                          value={adminAdjustState.newTotal}
                                          onChange={(e) => setAdminAdjustState({ ...adminAdjustState, newTotal: e.target.value })}
                                          helperText={`Saldo atual: ${formatStockAmount(modalValues?.amount)} un. O sistema calculará a diferença.`}
                                          inputProps={{ min: 0, step: 'any' }}
                                        />
                                      </Grid>
                                    ) : (
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          label={adminAdjustState.mode === 'SAIDA' ? 'Quantidade a Baixar' : 'Quantidade a Acrescentar'}
                                          type="number"
                                          size="small"
                                          variant="outlined"
                                          fullWidth
                                          value={adminAdjustState.amount}
                                          onChange={(e) => setAdminAdjustState({ ...adminAdjustState, amount: e.target.value })}
                                          inputProps={{ min: 0.01, step: 'any' }}
                                        />
                                      </Grid>
                                    )}

                                    {((adminAdjustState.mode === 'CORRECAO' && Number(adminAdjustState.newTotal) > Number(modalValues?.amount || 0)) || adminAdjustState.mode === 'ENTRADA') && (
                                      <Grid item xs={12} sm={6}>
                                        <CurrencyTextField
                                          label="Custo Unitário (Opcional)"
                                          size="small"
                                          variant="outlined"
                                          decimalCharacter=","
                                          digitGroupSeparator="."
                                          currencySymbol="R$"
                                          outputFormat="string"
                                          fullWidth
                                          value={adminAdjustState.cost}
                                          onChange={(ev, val) => setAdminAdjustState({ ...adminAdjustState, cost: val })}
                                        />
                                      </Grid>
                                    )}

                                    <Grid item xs={12}>
                                      <TextField
                                        label="Justificativa do Ajuste (Obrigatório)"
                                        placeholder="Ex: Quebra, avaria, produto vencido, ajuste de contagem de inventário..."
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        required
                                        value={adminAdjustState.justification}
                                        onChange={(e) => setAdminAdjustState({ ...adminAdjustState, justification: e.target.value })}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box display="flex" justifyContent="flex-end" mt={1}>
                                        <Button
                                          onClick={() => handleExecuteAdjustment(modalValues.id)}
                                          color="primary"
                                          variant="outlined"
                                          startIcon={<SettingsIcon />}
                                          disabled={
                                            !adminAdjustState.justification.trim() ||
                                            (adminAdjustState.mode === 'CORRECAO'
                                              ? Number(adminAdjustState.newTotal) < 0 || Number(adminAdjustState.newTotal) === Number(modalValues?.amount)
                                              : Number(adminAdjustState.amount) <= 0)
                                          }
                                        >
                                          Confirmar Ajuste Administrativo
                                        </Button>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>
                      ))}

                    {/* ABA 3: FORMATOS DE VENDA FRACIONADOS */}
                    {modalTab === 3 && !values.parentProductId && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box
                            display="flex"
                            flexDirection={mobile ? 'column' : 'row'}
                            justifyContent="space-between"
                            alignItems={mobile ? 'stretch' : 'flex-start'}
                            mb={2}
                            style={{ gap: 12 }}
                          >
                            <Box style={{ flex: 1 }}>
                              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Formatos de Venda Fracionados
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Cadastre unidades avulsas (ex: lata avulsa, dose, fração) vendidas a partir deste produto com controle de estoque automático.
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              size="medium"
                              style={{
                                minWidth: 150,
                                height: 40,
                                whiteSpace: 'nowrap',
                                fontWeight: 600,
                                flexShrink: 0,
                                alignSelf: mobile ? 'stretch' : 'auto',
                              }}
                              startIcon={<AddCircleIcon />}
                              onClick={() => {
                                setEditingFormatData(null);
                                setEditingFormatIndex(null);
                                setOpenFormatModal(true);
                              }}
                              disableElevation
                            >
                              Novo Formato
                            </Button>
                          </Box>
                        </Grid>

                        {(!values.childFormats || values.childFormats.length === 0) ? (
                          <Grid item xs={12}>
                            <Paper
                              style={{
                                padding: 32,
                                textAlign: 'center',
                                backgroundColor: '#f8fafc',
                                border: '2px dashed #cbd5e1',
                                borderRadius: 8,
                              }}
                              elevation={0}
                            >
                              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Nenhum formato de venda cadastrado para este produto.
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
                                Permite vender porções ou latas avulsas a partir deste fardo/embalagem sem duplicar estoque.
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<AddCircleIcon />}
                                onClick={() => {
                                  setEditingFormatData(null);
                                  setEditingFormatIndex(null);
                                  setOpenFormatModal(true);
                                }}
                              >
                                Adicionar Formato de Venda
                              </Button>
                            </Paper>
                          </Grid>
                        ) : (
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              {(values.childFormats || []).map((format, index) => (
                                <Grid item xs={12} sm={6} key={format.id || `child-${index}`}>
                                  <ProductSaleFormatCard
                                    format={format}
                                    onEdit={() => {
                                      setEditingFormatData(format);
                                      setEditingFormatIndex(index);
                                      setOpenFormatModal(true);
                                    }}
                                    onDelete={
                                      !format.id
                                        ? () => {
                                          const updated = [...(values.childFormats || [])];
                                          updated.splice(index, 1);
                                          setFieldValue('childFormats', updated);
                                        }
                                        : undefined
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </DialogContent>

                  <DialogActions
                    style={{
                      padding: mobile ? '12px 16px' : '12px 24px',
                      backgroundColor: '#fbfcfd',
                      borderTop: '1px solid #e0e0e0',
                      flexDirection: mobile ? 'column' : 'row',
                      alignItems: 'stretch',
                      gap: mobile ? 8 : 0,
                    }}
                  >
                    {mobile ? (
                      <>
                        {/* Primary Action full-width */}
                        <Button
                          onClick={() => submitForm()}
                          color="primary"
                          variant="contained"
                          disableElevation
                          fullWidth
                          style={{ height: 44, fontWeight: 600 }}
                        >
                          Salvar Alterações
                        </Button>

                        {/* Secondary Actions Side-by-Side */}
                        <Box display="flex" width="100%" style={{ gap: 8 }}>
                          <Button
                            onClick={() => setPrintSingleProduct(values)}
                            color="primary"
                            variant="outlined"
                            startIcon={<PrintIcon fontSize="small" />}
                            fullWidth
                            style={{ height: 38, fontSize: '0.85rem', fontWeight: 500 }}
                          >
                            Imprimir Etiqueta
                          </Button>
                          <Button
                            onClick={() => {
                              handleModalUpdate();
                              setModalValues(null);
                            }}
                            variant="outlined"
                            color="default"
                            fullWidth
                            style={{ height: 38, fontSize: '0.85rem', fontWeight: 500 }}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => setPrintSingleProduct(values)}
                          color="primary"
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          style={{ marginRight: 'auto' }}
                        >
                          Imprimir Etiqueta
                        </Button>
                        <Button onClick={() => { handleModalUpdate(); setModalValues(null); }}>
                          Cancelar
                        </Button>
                        <Button
                          onClick={() => submitForm()}
                          color="primary"
                          variant="contained"
                          disableElevation
                          style={{ fontWeight: 600 }}
                        >
                          Salvar Alterações
                        </Button>
                      </>
                    )}
                  </DialogActions>

                  <SupplierCreateModal
                    open={modalSupplier}
                    initialName={newSupplierName}
                    onClose={() => setModalSupplier(false)}
                    onSuccess={(newSupplier) => {
                      suppliersLoad();
                      setFieldValue('supplier', newSupplier.id);
                      setModalSupplier(false);
                    }}
                  />

                  <ProductSaleFormatModal
                    open={openFormatModal}
                    onClose={() => {
                      setOpenFormatModal(false);
                      setEditingFormatData(null);
                      setEditingFormatIndex(null);
                    }}
                    initialData={editingFormatData}
                    parentCost={Number(values.cost) || 0}
                    parentName={values.name || ''}
                    parentUnit={values.unit || 'UN'}
                    category={values.category}
                    categories={categoryInfo.data || []}
                    onBarcodeRegenerated={(newBarCode) => {
                      if (editingFormatIndex !== null && editingFormatIndex >= 0) {
                        const current = [...(values.childFormats || [])];
                        if (current[editingFormatIndex]) {
                          current[editingFormatIndex] = {
                            ...current[editingFormatIndex],
                            barCode: newBarCode,
                          };
                          setFieldValue('childFormats', current);
                        }
                        if (modalValues && modalValues.childFormats && modalValues.childFormats[editingFormatIndex]) {
                          modalValues.childFormats[editingFormatIndex].barCode = newBarCode;
                        }
                        filterLoad({ params: search });
                      }
                    }}
                    onSave={(formatData) => {
                      const current = [...(values.childFormats || [])];
                      if (editingFormatIndex !== null && editingFormatIndex >= 0) {
                        current[editingFormatIndex] = formatData;
                      } else {
                        current.push(formatData);
                      }
                      setFieldValue('childFormats', current);
                    }}
                  />
                </Form>
              );
            }}
          </Formik>
        </Dialog>
      )}
      <UIDialogConfirm
        open={alert}
        close={closeAlert}
        data={addAmountInfo.loading || addAmountInfo.data || addAmountInfo.error ? addAmountInfo :
          updateInfo.loading || updateInfo.data || updateInfo.error ? updateInfo :
            adjustInfo}
        setrequestinfo={closeAlert}
        messageSucess={
          (addAmountInfo.data && (addAmountInfo.data.message || 'Quantidade adicionada com sucesso')) ||
          (updateInfo.data && (updateInfo.data.message || 'Produto atualizado com sucesso')) ||
          (adjustInfo.data && (adjustInfo.data.message || 'Ajuste realizado com sucesso')) ||
          'Operação realizada com sucesso'
        }
        messageError={
          (addAmountInfo.error && 'Ocorreu um erro ao adicionar quantidade') ||
          (updateInfo.error && 'Ocorreu um erro ao atualizar o produto') ||
          (adjustInfo.error && 'Ocorreu um erro ao realizar o ajuste') ||
          'Ocorreu um erro inesperado'
        }
      />
      <ProductImportModal
        open={importModal}
        handleClose={() => setImportModal(false)}
        onReload={() => filterLoad({ params: search })}
      />
      <ProductSinglePrintModal
        open={Boolean(printSingleProduct)}
        product={printSingleProduct}
        handleClose={() => setPrintSingleProduct(null)}
      />
      <CategoryBulkPrintModal
        open={bulkPrintModal}
        handleClose={() => {
          setBulkPrintModal(false);
          setBulkPrintItems(null);
        }}
        referenceType="p"
        initialItems={bulkPrintItems}
      />
      <ScaleExportModal
        open={scaleExportModal}
        handleClose={() => {
          setScaleExportModal(false);
          setScaleExportItems(null);
        }}
        initialItems={scaleExportItems}
      />
      <UIBatchActionBar
        selectedCount={selectedIds.length}
        totalCount={filterInfo.data ? filterInfo.data.length : 0}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleSelectAll}
        onCatalogVisibility={handleBatchCatalogVisibility}
        onPrintLabels={handleBatchPrintLabels}
        onScaleExport={handleBatchScaleExport}
        loading={updateInfo.loading}
        itemLabel="produtos"
      />
    </>
  );
};

export default ProductSearch;